import { Component, Renderer2, effect, inject, input, untracked } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Store } from '@ngrx/store';

import { PageMetaService } from '@fcom/common/services/page-meta/page-meta.service';
import { NavigationMenuService } from '@fcom/common/services/navigation-menu/navigation-menu.service';
import * as GlobalBookingActions from '@fcom/common/store/actions/global-booking.actions';
import { AppState, ORIGIN_SELECTOR_CONFIG } from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';
import { CmsPage } from '@fcom/core-api';

@Component({
  selector: 'cms-page',
  template: '<ng-content />',
  host: { class: 'is-hidden' },
  standalone: false,
})
export class CmsPageComponent {
  private pageMetaService = inject(PageMetaService);
  private renderer = inject(Renderer2);
  private navigationMenuService = inject(NavigationMenuService);
  private activatedRoute = inject(ActivatedRoute);
  private store$ = inject<Store<AppState>>(Store);
  private languageService = inject(LanguageService);

  content = input<CmsPage>();

  constructor() {
    effect(() => {
      const content = this.content();
      untracked(() => {
        if (typeof content.isHeaderVisible !== 'undefined') {
          this.navigationMenuService.setHeaderVisibility(content.isHeaderVisible);
        }
        if (typeof content.isFooterVisible !== 'undefined') {
          this.navigationMenuService.setFooterVisibility(content.isFooterVisible);
        }
        const originFromQuery = this.activatedRoute.snapshot.queryParamMap.get('origin')?.toUpperCase();
        const countryCode = this.languageService.ddsLocaleValue?.countrySite?.toUpperCase() ?? 'INT';
        const allowedOrigins = ORIGIN_SELECTOR_CONFIG[countryCode];
        const isValidOrigin = !!originFromQuery && !!allowedOrigins?.includes(originFromQuery);
        const originCode = isValidOrigin ? originFromQuery : (content.defaultOriginLocationCode ?? 'HEL');
        this.pageMetaService.setDefaultOriginLocationCode(originCode);
        if (originFromQuery) {
          // Dispatch the resolved origin so the booking widget shows the correct origin
          // without falling back to geolocation: either the valid param or the site default.
          this.store$.dispatch(GlobalBookingActions.setOrigin({ origin: originCode }));
        }
        this.pageMetaService.setTitle(content.seoTitle || content.title);
        // Make the pageMetaService to sync the latest metaData
        this.pageMetaService.moveMetaAndLinksToHead(content.metaData, this.renderer);
      });
    });
  }
}
